import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { startSupportConversation, getMessages, sendMessage, markRead } from "../../api/chatService";
import "./clientTheme.css";
import "./SupportChatModal.css";

const POLL_INTERVAL_MS = 4000;

function getMessageTypePreviews(t) {
  return {
    2: t("photoMessagePreview"),
    3: t("productMessagePreview"),
    4: t("videoMessagePreview"),
    5: t("orderMessagePreview"),
    6: t("locationMessagePreview"),
  };
}

function formatTime(value) {
  return new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function HeadsetIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5Z" />
      <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5Z" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

export default function SupportChatModal({ onClose }) {
  const { t } = useTranslation(["supportChatModal", "common"]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);
  const endRef = useRef(null);
  const conversationIdRef = useRef(null);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    startSupportConversation()
      .then((conversation) => {
        if (cancelled) return;
        conversationIdRef.current = conversation.conversationId;
        setConversationId(conversation.conversationId);
        return getMessages(conversation.conversationId).then((result) => {
          if (cancelled) return;
          setMessages(result.items.slice().reverse());
          return markRead(conversation.conversationId);
        });
      })
      .catch(() => {
        if (!cancelled) setError(t("startConversationError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!conversationId) return;
    pollRef.current = setInterval(() => {
      getMessages(conversationId)
        .then((result) => {
          setMessages(result.items.slice().reverse());
          markRead(conversationId).catch(() => {});
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [conversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !conversationId || sending) return;

    setSending(true);
    setDraft("");
    try {
      const message = await sendMessage(conversationId, { messageText: text });
      setMessages((prev) => [...prev, message]);
    } catch {
      setDraft(text);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="client-support-chat-overlay" onMouseDown={onClose}>
      <div className="client-support-chat-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="client-support-chat-header">
          <div className="client-support-chat-avatar">
            <HeadsetIcon />
          </div>
          <span className="client-support-chat-title">{t("chatTitle")}</span>
          <button type="button" className="client-support-chat-close" aria-label={t("common:close")} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="client-support-chat-body">
          {loading ? (
            <div className="client-support-chat-placeholder">{t("common:loading")}</div>
          ) : error ? (
            <div className="client-support-chat-placeholder">{error}</div>
          ) : messages.length === 0 ? (
            <div className="client-support-chat-placeholder">{t("emptyStatePlaceholder")}</div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.messageId}
                  className={`client-support-chat-bubble ${message.isMine ? "mine" : ""}`}
                >
                  {message.isDeleted ? (
                    <span className="client-support-chat-deleted">{t("messageDeletedLabel")}</span>
                  ) : message.messageType === 2 && message.attachmentUrl ? (
                    <img src={message.attachmentUrl} alt="" className="client-support-chat-image" />
                  ) : message.messageText ? (
                    <span>{message.messageText}</span>
                  ) : (
                    <span>{getMessageTypePreviews(t)[message.messageType] || t("genericMessageLabel")}</span>
                  )}
                  <span className="client-support-chat-time">{formatTime(message.sentAt)}</span>
                </div>
              ))}
              <div ref={endRef} />
            </>
          )}
        </div>

        <form className="client-support-chat-compose" onSubmit={handleSend}>
          <input
            type="text"
            placeholder={t("messageInputPlaceholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="client-support-chat-input"
            disabled={loading || !!error}
            autoFocus
          />
          <button
            type="submit"
            className="client-support-chat-send"
            disabled={sending || !draft.trim() || loading || !!error}
            aria-label={t("sendButtonLabel")}
          >
            <SendIcon />
          </button>
        </form>
      </div>
    </div>
  );
}
